/**
 * KG cart — uses cart attribute `current_collection`.
 * On collection visit: set attribute, remove lines not available at that location.
 */
const KG_CART_SYNC_ENABLED = true;
const KG_ATTR_COLLECTION = "current_collection";
const KG_JSON_HEADERS = {
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest"
};

let kgCartQueue = Promise.resolve();

function kgProp(item, key) {
  const p = item?.properties;
  if (!p) return undefined;
  if (Array.isArray(p)) {
    return p.find((row) => row?.name === key)?.value;
  }
  return p[key];
}

function kgLineKey(item) {
  const key = item?.key;
  return typeof key === "string" && key.includes(":") ? key : null;
}

function kgLineInCollection(allTitles, collectionHandle) {
  if (!allTitles || !collectionHandle) return false;
  const list = allTitles
    .toString()
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return list.includes(collectionHandle);
}

function kgStockAt(item, collectionHandle) {
  const mapRaw = kgProp(item, "__pickup_location_map");
  if (!mapRaw) return null;

  const map = {};
  mapRaw.split(",").forEach((part) => {
    const i = part.lastIndexOf(":");
    if (i === -1) return;
    const h = part.slice(0, i).trim();
    const id = part.slice(i + 1).trim();
    if (h && id) map[h] = Number(id);
  });

  const locationId = map[collectionHandle];
  if (!locationId) return null;

  const invRaw = kgProp(item, "__location_inventories_json");
  if (!invRaw) return null;

  let inv = invRaw;
  if (typeof inv === "string") {
    try {
      inv = JSON.parse(inv);
      if (typeof inv === "string") inv = JSON.parse(inv);
    } catch {
      return null;
    }
  }

  const variantKey = String(item.variant_id);
  let rows = inv?.[variantKey] ?? inv?.[item.variant_id];
  if (rows == null && Array.isArray(inv)) {
    const row = inv.find((e) => Array.isArray(e) && String(e[0]) === variantKey);
    rows = row?.[1];
  }
  if (!rows) return null;

  if (Array.isArray(rows)) {
    const hit = rows.find((r) => Array.isArray(r) && Number(r[0]) === locationId);
    return hit ? Number(hit[1]) : null;
  }
  if (typeof rows === "object") {
    const q = rows[String(locationId)] ?? rows[locationId];
    return q == null ? null : Number(q);
  }
  return null;
}

function kgShouldRemoveLine(item, collectionHandle) {
  const titles = kgProp(item, "__all_collection_titles");
  if (titles && !kgLineInCollection(titles, collectionHandle)) {
    return true;
  }
  const stock = kgStockAt(item, collectionHandle);
  if (stock !== null && stock < item.quantity) {
    return true;
  }
  return false;
}

function kgEnqueue(task) {
  const run = kgCartQueue.then(task);
  kgCartQueue = run.catch(() => {});
  return run;
}

async function kgFetchJson(url, options) {
  const res = await fetch(url, options);
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    console.warn("[kg] cart request failed", res.status, body);
    return null;
  }
  return body;
}

function kgFetchCart() {
  return kgFetchJson(`${Shopify.routes.root}cart.js`, {
    headers: { "X-Requested-With": "XMLHttpRequest" }
  });
}

function kgFilterLineUpdates(cart, updates) {
  const live = new Set(
    (cart?.items || []).map((item) => kgLineKey(item)).filter(Boolean)
  );
  const safe = {};
  Object.entries(updates).forEach(([key, qty]) => {
    if (live.has(key)) safe[key] = 0;
  });
  return safe;
}

async function kgSetCurrentCollection(collectionHandle) {
  if (!collectionHandle) return null;
  return kgFetchJson(`${Shopify.routes.root}cart/update.js`, {
    method: "POST",
    headers: KG_JSON_HEADERS,
    body: JSON.stringify({
      attributes: { [KG_ATTR_COLLECTION]: collectionHandle }
    })
  });
}

async function kgRemoveUnavailableLines(cart, collectionHandle) {
  if (!cart?.items?.length || !collectionHandle) return cart;

  const updates = {};
  cart.items.forEach((item) => {
    if (!kgShouldRemoveLine(item, collectionHandle)) return;
    const key = kgLineKey(item);
    if (key) updates[key] = 0;
  });

  const safe = kgFilterLineUpdates(cart, updates);
  if (!Object.keys(safe).length) return cart;

  return (
    (await kgFetchJson(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: KG_JSON_HEADERS,
      body: JSON.stringify({ updates: safe })
    })) || cart
  );
}

function kgDispatchCartChange(cart) {
  if (!cart) return;
  window.kgInitialCart = cart;
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:change", {
      bubbles: true,
      detail: { baseEvent: "kg:collection-prune", cart }
    })
  );
}

async function kgRunCollectionSync() {
  return kgEnqueue(async () => {
    const fromPage = window.kgCurrentCollection;
    let cart = await kgFetchCart();
    if (!cart) return null;

    let collectionHandle =
      fromPage || cart.attributes?.[KG_ATTR_COLLECTION] || null;

    if (fromPage) {
      cart = (await kgSetCurrentCollection(fromPage)) || cart;
      collectionHandle = fromPage;
    }

    if (!collectionHandle || !cart.items?.length) return cart;

    cart = (await kgRemoveUnavailableLines(cart, collectionHandle)) || cart;
    kgDispatchCartChange(cart);
    return cart;
  });
}

function kgInitCollectionSync() {
  return kgRunCollectionSync();
}

if (KG_CART_SYNC_ENABLED) {
  window.kgInitCollectionSync = kgInitCollectionSync;
  document.addEventListener("DOMContentLoaded", kgInitCollectionSync);
}

// function kgInitCollectionSync() {
//   kgRunCollectionSync();
// }

// if (KG_CART_SYNC_ENABLED) {
//   document.addEventListener("DOMContentLoaded", kgInitCollectionSync);
//   document.addEventListener("cart:change", (event) => {
//     console.log(window.kgCurrentCollection);
//     if (event.detail?.baseEvent !== "variant:add" || !window.kgCurrentCollection) return;
//     kgInitCollectionSync();
//     // console.log("BBBBBBBBBBBBBBBBBBBBBBBBB");
//   });
// }